import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getSocket } from '../../../../socket';
import { getTimerColors } from 'utils/timerColors';
import ActivityParticipant from '../../ActivityParticipant';
import DebateAssignments from '../../AdminCards/DebateAssignments';
import useSortableList from 'components/general/useSortableList';
import Limits from './SharedComponents/Limits';
import Clock from './PathwayComponents/Clock';
import { getActivityTimingLimits } from 'utils/activityTiming';
import { getDebateTurns, isDebaterSegment } from 'utils/debateTurns';
import useDebateSpeaker from '../../useDebateSpeaker';
import DebateTurnTicks from '../../DebateTurnTicks';

const activityTypeFromItem = (item) => {
  const titleLines = String(item?.item_title || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const title = String(titleLines.length > 1 ? titleLines.slice(1).join(' ') : item?.pathwayAttempt ? titleLines[0] : '').toLowerCase();
  if (title.includes('panel discussion') || title.includes('pannel discussion')) return 'panelDiscussion';
  if (title.includes('debate')) return 'debate';
  return '';
};

const displayName = (user) => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'TBA';
const identityIds = user => [user?.userId, user?.id, user?._id, user?.tmAuthUserId, user?.localUserId]
  .filter(Boolean).map(String);
const toActivityDraft = (activity) => activity ? ({
  ...activity,
  segments: (activity.segments || []).map((segment) => ({
    ...segment,
    speakerId: segment.speaker?.userId || segment.speakerId || '',
  })),
}) : activity;
const activityPayload = ({ turnTimers, ...draft }) => ({
  ...draft,
  segments: (draft.segments || []).map(({ timer, ...segment }) => segment),
  panelists: (draft?.panelists || []).map((user) => user.userId || user),
  sides: Object.fromEntries(['pro', 'con'].map(side => [side, (draft?.sides?.[side] || []).map(user => user?.userId || user || '')])),
});

function SegmentTimer({ item, segment, meeting, isTimerManager, isLocked, getMeeting, onColorsChange, onStart, onStop, liveState, onTimerStateChange }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const url = process.env.REACT_APP_URL;
  const [time, setTime] = useState(Number(segment?.timer?.time || 0));
  const [isPlaying, setIsPlaying] = useState(false);
  const [savedTimer, setSavedTimer] = useState(segment?.timer || null);
  const [starting, setStarting] = useState(false);
  const meetingId = meeting?.meetingDetails?.id || meeting?._id || item?.meeting;
  const segmentId = String(segment?.id || '');
  const identityIds = [authUser?.user?.id, authUser?.user?._id, authUser?.user?.tmAuthUserId, authUser?.user?.localUserId]
    .map((value) => String(value || '').trim()).filter(Boolean);
  const managerId = String(meeting?.meetingDetails?.specialAssignments?.timerManager || '').trim();
  const canControl = Boolean(isTimerManager && managerId && identityIds.includes(managerId) && authUser?.token);
  const colors = useMemo(() => getTimerColors(time, getActivityTimingLimits({
    type: 'debate', segments: [{ green: segment?.green, amber: segment?.amber, red: segment?.red }],
  }), { background: 'rgba(0, 0, 0, 0)', foreground: 'var(--foreground)' }), [
    time,
    segment?.green,
    segment?.amber,
    segment?.red,
  ]);

  useEffect(() => {
    onColorsChange?.(colors);
  }, [colors, onColorsChange]);

  useEffect(() => {
    setTime(Number(segment?.timer?.time || 0));
    setSavedTimer(segment?.timer?._id ? { _id: segment.timer._id } : null);
  }, [segmentId, segment?.timer?._id, segment?.timer?.time]);

  useEffect(() => {
    if (!liveState) return;
    setTime(Number(liveState.time) || 0);
    setIsPlaying(Boolean(liveState.isPlaying));
  }, [liveState]);

  useEffect(() => {
    if (!meetingId || !item?.agendaItemId || !segmentId) return undefined;
    const socket = getSocket();
    const matches = (payload) => String(payload?.meetingId || '') === String(meetingId)
      && String(payload?.agendaItemId || '') === String(item.agendaItemId)
      && String(payload?.segmentId || '') === segmentId;
    const onTick = (payload) => {
      if (!matches(payload)) return;
      const next = Number(payload.time);
      if (Number.isFinite(next)) setTime(Math.max(0, next));
    };
    const onState = (payload) => {
      if (!matches(payload)) return;
      const next = Number(payload.time);
      if (Number.isFinite(next)) setTime(Math.max(0, next));
      setIsPlaying(Boolean(payload.isPlaying));
    };
    const onReset = (payload) => {
      if (!matches(payload)) return;
      setTime(0);
      setIsPlaying(false);
      setSavedTimer(null);
    };
    socket.on('meeting:timer-tick', onTick);
    socket.on('meeting:timer-state', onState);
    socket.on('meeting:timer-reset', onReset);
    socket.emit('meeting:join', String(meetingId));
    return () => {
      socket.off('meeting:timer-tick', onTick);
      socket.off('meeting:timer-state', onState);
      socket.off('meeting:timer-reset', onReset);
    };
  }, [meetingId, item?.agendaItemId, segmentId]);

  const emitState = (playing) => {
    const payload = { meetingId, agendaItemId: item.agendaItemId, segmentId, isPlaying: playing, time };
    onTimerStateChange?.(payload);
    getSocket().emit('meeting:timer-state', payload);
  };

  const save = async (nextTime) => {
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${authUser?.token || ''}` };
    const path = `${url}/timer/${item.agendaItemId}/segment/${encodeURIComponent(segmentId)}`;
    const method = savedTimer?._id ? 'PATCH' : 'PUT';
    const response = await fetch(path, {
      method,
      headers,
      body: JSON.stringify({ time: nextTime, name: displayName(segment?.speaker) }),
    });
    if (!response.ok) throw new Error('Unable to save the segment timer.');
    const timer = await response.json();
    setSavedTimer(timer);
    await Promise.resolve(getMeeting?.());
  };

  const stop = async () => {
    setIsPlaying(false);
    emitState(false);
    try {
      await Promise.all([save(time), onStop?.()]);
    } catch (error) {
      toast.error(error.message, { position: 'bottom-left' });
    }
  };
  return (
    <div className="activity-segment-timer mt-2" style={{ color: colors.foreground }}>
      <Clock time={time} />
      <div className="d-flex justify-content-center align-items-center gap-2">
      {canControl && !isLocked &&
        <Button variant="link"
          className={`agenda-modal-timer-button ${isPlaying ? 'agenda-modal-timer-button--pause' : 'agenda-modal-timer-button--start'}`}
          aria-label={isPlaying ? 'Stop segment timer' : 'Start segment timer'}
          disabled={starting}
          onClick={async () => {
            if (isPlaying) {
              await stop();
              return;
            }
            setStarting(true);
            try {
              await onStart?.();
              setIsPlaying(true);
              emitState(true);
            } catch (error) {
              toast.error(error.message || 'Unable to start the segment timer.', { position: 'bottom-left' });
            } finally {
              setStarting(false);
            }
          }}>
          <i className={`bi ${isPlaying ? 'bi-stop-fill' : 'bi-play-fill'} agenda-modal-timer-button-icon`} aria-hidden="true" />
          <span className="agenda-modal-timer-button-text">{isPlaying ? 'STOP' : 'START'}</span>
        </Button>
      }
      </div>
    </div>
  );
}

export default function PathwayActivity({ item, meeting, getMeeting, canEdit, canManageAssignments = canEdit, isTimerManager, isLocked, onSegmentColorsChange, onFirstSegmentStart, onLastSegmentStop }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const url = process.env.REACT_APP_URL;
  const detectedType = activityTypeFromItem(item);
  const [savedActivity, setSavedActivity] = useState(null);
  const activitySource = useRef(item?.activity);
  activitySource.current = item?.activity;
  const currentActivity = savedActivity?.source === item?.activity && savedActivity?.agendaItemId === item?.agendaItemId
    ? savedActivity.activity : item?.activity || null;
  const turns = useMemo(() => getDebateTurns(currentActivity, item?.assignedUser), [currentActivity, item?.assignedUser]);
  const [templates, setTemplates] = useState([]);
  const [draft, setDraft] = useState(currentActivity);
  const [showEditor, setShowEditor] = useState(false);
  const { index: activeIndex, select: selectTurn, running, liveState, updateTimerState } = useDebateSpeaker({
    item, meeting, turns, canControl: isTimerManager && !isLocked,
  });
  // Speaker navigation is a timer control, so only the Timer Manager gets it.
  const canNavigate = Boolean(isTimerManager);
  const [saving, setSaving] = useState(false);
  const saveQueue = useRef(Promise.resolve());
  const savedPayload = useRef('');
  const previousActivityPayload = useRef(currentActivity ? JSON.stringify(activityPayload(toActivityDraft(currentActivity))) : '');
  const [activeSegmentColors, setActiveSegmentColors] = useState({
    background: 'var(--modal-bg)',
    foreground: 'var(--foreground)',
  });
  useEffect(() => {
    onSegmentColorsChange?.(currentActivity?.segments?.length ? activeSegmentColors : {
      background: 'rgba(0, 0, 0, 0)', foreground: 'var(--foreground)',
    });
  }, [activeSegmentColors, onSegmentColorsChange, currentActivity?.segments?.length]);
  const users = useMemo(() => {
    const entries = [
      ...(Array.isArray(meeting?.roleAssignments) ? meeting.roleAssignments.map((assignment) => assignment?.user) : []),
      ...(Array.isArray(meeting?.meetingDetails?.attendance) ? meeting.meetingDetails.attendance : []),
    ].filter((user) => user && (user.id || user._id));
    return Array.from(new Map(entries.map((user) => [String(user.id || user._id), user])).values());
  }, [meeting]);

  useEffect(() => {
    const nextDraft = toActivityDraft(currentActivity);
    const nextPayload = nextDraft ? JSON.stringify(activityPayload(nextDraft)) : '';
    const previousPayload = previousActivityPayload.current;
    if (!showEditor || (!nextDraft && previousPayload)) {
      previousActivityPayload.current = nextPayload;
      setDraft(nextDraft);
      if (!nextDraft && previousPayload) setShowEditor(false);
    } else if (!saving && nextPayload !== previousPayload) {
      savedPayload.current = nextPayload;
      setDraft(current => {
        const payload = current ? JSON.stringify(activityPayload(current)) : '';
        return payload === previousPayload ? nextDraft : current;
      });
      previousActivityPayload.current = nextPayload;
    } else if (saving) {
      previousActivityPayload.current = nextPayload;
    }
  }, [currentActivity, showEditor, saving]);

  useEffect(() => {
    if (!showEditor || !authUser?.token || !url) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${url}/activity-templates?type=${encodeURIComponent(draft?.type || detectedType)}`, {
          headers: { Authorization: `Bearer ${authUser.token}` },
        });
        if (!response.ok) throw new Error('Activity templates could not be loaded.');
        const data = await response.json();
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) toast.error(error.message, { position: 'bottom-left' });
      }
    })();
    return () => { cancelled = true; };
  }, [showEditor, authUser?.token, url, draft?.type, detectedType]);

  const initializeDraft = () => {
    if (!authUser?.token) {
      toast.error('please authenticate, or insufficient rights.', { position: 'bottom-left' });
      return;
    }
    if (!canEdit) {
      toast.error('please authenticate, or insufficient rights.', { position: 'bottom-left' });
      return;
    }
    const next = toActivityDraft(currentActivity) || { type: detectedType, templateId: '', templateName: '', panelists: [], segments: [] };
    savedPayload.current = currentActivity ? JSON.stringify(activityPayload(next)) : '';
    setDraft(next);
    setShowEditor(true);
  };

  const saveActivity = (next = draft) => {
    if (!canEdit || isLocked || !authUser?.token) {
      toast.error('Please authenticate, or insufficient rights.', { position: 'bottom-left' });
      return;
    }
    if (!next || next.segments?.some(segment => !segment.title?.trim())) {
      toast.error('Enter a title for every segment before saving.', { position: 'bottom-left' });
      return;
    }
    const payload = JSON.stringify(activityPayload(next));
    setSaving(true);
    // Serialize blur/click saves without replacing edits made while a request is pending.
    const task = saveQueue.current.then(async () => {
      if (savedPayload.current === payload) return true;
      try {
        const response = await fetch(`${url}/agendaItem/${item.agendaItemId}/activity`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authUser.token}` },
          body: `{"activity":${payload}}`,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Activity could not be saved.');
        if (!result.activity || !Array.isArray(result.activity.segments)) {
          throw new Error('The server returned an invalid saved activity.');
        }
        const savedDraft = toActivityDraft(result.activity);
        previousActivityPayload.current = JSON.stringify(activityPayload(savedDraft));
        setSavedActivity({
          agendaItemId: item.agendaItemId,
          source: activitySource.current,
          activity: {
            ...result.activity,
            turnTimers: activitySource.current?.turnTimers || [],
            segments: result.activity.segments.map(segment => ({
              ...segment,
              timer: activitySource.current?.segments?.find(entry => entry.id === segment.id)?.timer || null,
            })),
          },
        });
        setDraft(current => JSON.stringify(activityPayload(current)) === payload ? savedDraft : current);
        savedPayload.current = payload;
        await Promise.resolve(getMeeting?.());
        return true;
      } catch (error) {
        toast.error(error.message, { position: 'bottom-left' });
        return false;
      }
    });
    saveQueue.current = task;
    void task.then(() => {
      if (saveQueue.current === task) setSaving(false);
    });
    return task;
  };

  const saveAndClose = async () => {
    if (await saveActivity()) setShowEditor(false);
  };

  const applyTemplate = (templateId) => {
    const template = templates.find((entry) => String(entry._id) === String(templateId));
    if (!template) return;
    setDraft((current) => ({
      ...current,
      type: template.type,
      templateId: template._id,
      templateName: template.name,
      panelists: current?.panelists || [],
      segments: (template.segments || []).map((segment, index) => ({
        ...segment,
        id: `segment-${index + 1}`,
        speakerId: current?.segments?.[index]?.speakerId || current?.segments?.[index]?.speaker?.userId || '',
      })),
    }));
  };

  const updateSegment = (index, values) => setDraft((current) => ({
    ...current,
    segments: current.segments.map((segment, segmentIndex) => segmentIndex === index ? { ...segment, ...values } : segment),
  }));
  const moveSegment = (from, to) => {
    if (from === to || to < 0) return;
    const segments = [...(draft?.segments || [])];
    if (from >= segments.length || to >= segments.length) return;
    const [moved] = segments.splice(from, 1);
    segments.splice(to, 0, moved);
    const next = { ...draft, segments };
    setDraft(next);
    void saveActivity(next);
  };
  const segmentSort = useSortableList({ onMove: moveSegment });
  if (!detectedType && !currentActivity) return null;

  const selectedSegment = turns[activeIndex];
  const segmentSpeaker = selectedSegment?.speaker || (/^moderator$/i.test(selectedSegment?.role || '') ? item?.assignedUser : null);
  const speakerIds = identityIds(segmentSpeaker);
  const selectedSide = selectedSegment?.side || ['pro', 'con'].find(side => (currentActivity?.sides?.[side] || [])
    .some(user => identityIds(user).some(id => speakerIds.includes(id))));
  const sideLabel = selectedSide ? selectedSide.toUpperCase() : /^(pro|con)(?:\s|$)/i.exec(selectedSegment?.role || '')?.[1]?.toUpperCase();
  const panelists = currentActivity?.panelists || [];

  return (
    <section className={`activity-panel border rounded p-2 mx-3 my-2 text-start${currentActivity?.type === 'debate' ? ' activity-panel-debate' : ''}`} aria-label="Pathway activity">
      {currentActivity?.type === 'panelDiscussion' && (
        <div>
          <strong>Panelists</strong>
          {panelists.length
            ? <ul className="activity-participant-list">{panelists.map((user) => (
              <li key={user.userId}><ActivityParticipant user={user} meeting={meeting} /></li>
            ))}</ul>
            : <div>No panelists assigned.</div>}
        </div>
      )}
      {currentActivity?.type === 'debate' && (
        <div>
          {currentActivity.segments?.length > 0 && (
            <section className="activity-active-segment p-3 mb-3" aria-label="Active segment"
              style={{ background: activeSegmentColors.background, color: activeSegmentColors.foreground }}>
              <div className={`activity-segment-navigation${canNavigate ? '' : ' is-read-only'}`}>
                {canNavigate && <button type="button" className="activity-segment-arrow" aria-label="Previous" title="Previous"
                  disabled={activeIndex <= 0 || running || isLocked} onClick={() => selectTurn(activeIndex - 1)}>
                  <i className="bi bi-chevron-left" aria-hidden="true" />
                </button>}
                <div className="activity-segment-columns">
                <div className="activity-segment-speaker">
                  <ActivityParticipant user={segmentSpeaker} meeting={meeting} avatarOnly />
                </div>
                <div className="activity-segment-details">
                  <small>Segment {selectedSegment.segmentIndex + 1} of {currentActivity.segments.length}</small>
                  <h3>{selectedSegment.title}</h3>
                  {selectedSegment.turnCount && <small>Debater {selectedSegment.turnIndex + 1} of {selectedSegment.turnCount}</small>}
                  {['PRO', 'CON'].includes(sideLabel)
                    ? <div><span className={`debate-side-badge debate-side-badge-${sideLabel.toLowerCase()}`}>{sideLabel}</span></div>
                    : <div>{sideLabel || selectedSegment.role}</div>}
                  <div>{segmentSpeaker ? displayName(segmentSpeaker) : 'Speaker not assigned'}</div>
                  {selectedSegment.requiresDebaters && <div role="status">Choose debaters in Assignments to enable individual timers.</div>}
                  <Limits item={{ agendaEvent: getActivityTimingLimits({ type: 'debate', segments: [selectedSegment] }) }} readOnly compact />
                  <SegmentTimer
                    key={selectedSegment.id}
                    item={item}
                    segment={selectedSegment}
                    meeting={meeting}
                    isTimerManager={isTimerManager && !selectedSegment.requiresDebaters}
                    isLocked={isLocked}
                    getMeeting={getMeeting}
                    liveState={liveState}
                    onTimerStateChange={updateTimerState}
                    onColorsChange={setActiveSegmentColors}
                    onStart={activeIndex === 0 ? onFirstSegmentStart : undefined}
                    onStop={activeIndex === turns.length - 1 ? onLastSegmentStop : undefined}
                  />
                </div>
                </div>
                {canNavigate && <button type="button" className="activity-segment-arrow" aria-label="Next" title="Next"
                  disabled={activeIndex >= turns.length - 1 || running || isLocked} onClick={() => selectTurn(activeIndex + 1)}>
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </button>}
              </div>
              <DebateTurnTicks className="mt-3" turns={turns} index={activeIndex} onSelect={selectTurn}
                readOnly={!canNavigate} disabled={running || isLocked} />
            </section>
          )}
          {!showEditor && <DebateAssignments collapsible item={item} meeting={meeting} getMeeting={getMeeting} canManage={canManageAssignments && !isLocked} />}
        </div>
      )}
      {canEdit && !isLocked && !showEditor && <Button size="sm" variant="outline-secondary" className="mt-2" onClick={initializeDraft}>
        {currentActivity ? 'Edit activity' : `Set up ${detectedType === 'debate' ? 'debate' : 'panel discussion'}`}
      </Button>}
      {showEditor && (
        <div className="mt-2" onBlur={(event) => {
          if (event.relatedTarget?.closest('[data-save-activity]')) return;
          if (event.target.matches('input, select, textarea') && !draft?.segments?.some(segment => !segment.title?.trim())) void saveActivity();
        }}>
          <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
            <span className="small" role="status">{saving ? 'Saving...' : draft?.segments?.some(segment => !segment.title?.trim()) ? 'Enter segment titles to save.' : ''}</span>
            <Button size="sm" disabled={isLocked} data-save-activity aria-label="Save activity" title="Save activity"
              onClick={() => void saveAndClose()}><i className="bi bi-floppy" aria-hidden="true" /></Button>
            <Button size="sm" variant="outline-secondary" disabled={saving} aria-label="Close activity editor"
              onClick={() => setShowEditor(false)}><i className="bi bi-x-lg me-1" aria-hidden="true" />Close</Button>
          </div>
          <Form.Group className="mb-2">
            <Form.Label>Activity template</Form.Label>
            <Form.Select value={draft?.templateId || ''} onChange={(event) => applyTemplate(event.target.value)}>
              <option value="">Choose a template</option>
              {templates.map((template) => <option value={template._id} key={template._id}>{template.name}</option>)}
            </Form.Select>
          </Form.Group>
          {draft?.type === 'panelDiscussion' && (
            <Form.Group className="mb-2">
              <Form.Label>Panelists</Form.Label>
              <div className="border rounded p-2" role="group" aria-label="Select panelists">
                {users.map((user) => {
                  const userId = String(user.tmAuthUserId || user.id || user._id);
                  const isSelected = (draft.panelists || []).some((panelist) => String(panelist.userId) === userId);
                  return (
                    <Form.Check
                      key={userId}
                      type="checkbox"
                      id={`panelist-${userId}`}
                      className="py-1"
                      label={displayName(user)}
                      checked={isSelected}
                      onChange={(event) => {
                        const panelists = event.target.checked
                          ? [...(draft.panelists || []), {
                            userId,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            avatar: user.avatarUrl || user.avatar,
                          }]
                          : (draft.panelists || []).filter((panelist) => String(panelist.userId) !== userId);
                        setDraft({ ...draft, panelists });
                      }}
                    />
                  );
                })}
                {users.length === 0 && <small>No club members are available to select.</small>}
              </div>
            </Form.Group>
          )}
          {draft?.type === 'debate' && (
            <Form.Group className="mb-2">
              <Form.Label htmlFor={`speaking-order-${item.agendaItemId}`}>Debater speaking order</Form.Label>
              <Form.Select id={`speaking-order-${item.agendaItemId}`}
                aria-label="Debater speaking order"
                value={draft.speakingOrder || 'alternate'} disabled={isLocked}
                onChange={event => {
                  const speakingOrder = event.target.value;
                  const next = {
                    ...draft,
                    speakingOrder,
                    segments: (draft.segments || []).map(segment => ({ ...segment, speakingOrder })),
                  };
                  setDraft(next);
                  void saveActivity(next);
                }}>
                <option value="alternate">Alternate Pro 1, Con 1, Pro 2, Con 2</option>
                <option value="grouped">All Pro debaters, then all Con debaters</option>
              </Form.Select>
            </Form.Group>
          )}
          <div className="activity-segment-list" {...segmentSort.containerProps}>
          {(draft?.segments || []).map((segment, index) => <div
            className="activity-segment-card"
            key={segment.id || index}
            {...segmentSort.itemProps(index)}>
            <div className="activity-segment-card-header">
              <span className="activity-segment-handle" role="button" tabIndex={0}
                aria-label={`Drag to reorder segment ${index + 1}`} title="Drag to reorder"
                {...segmentSort.handleProps(index)}
                onKeyDown={(event) => {
                  if (event.altKey && event.key === 'ArrowUp') { event.preventDefault(); moveSegment(index, index - 1); }
                  if (event.altKey && event.key === 'ArrowDown') { event.preventDefault(); moveSegment(index, index + 1); }
                }}>⠿</span>
              <strong>Segment {index + 1}</strong>
              <Button size="sm" variant="outline-danger" className="ms-auto" aria-label={`Remove segment ${index + 1}`} title="Remove segment"
                onClick={() => {
                  const next = { ...draft, segments: draft.segments.filter((_, segmentIndex) => segmentIndex !== index) };
                  setDraft(next);
                  void saveActivity(next);
                }}><i className="bi bi-trash" aria-hidden="true" /></Button>
            </div>

            <Form.Control value={segment.title || ''} onChange={(event) => updateSegment(index, { title: event.target.value })} aria-label={`Segment ${index + 1} title`} />
            {draft.type === 'debate' ? <Form.Select className="mt-2" value={segment.role || ''}
              aria-label={`Segment ${index + 1} role`}
              onChange={event => updateSegment(index, { role: event.target.value, speakerId: '' })}>
              <option value="">Choose segment role</option>
              <option value="Moderator">Moderator</option>
              <option value="Debaters">Debaters</option>
              {segment.role && !['Moderator', 'Debaters'].includes(segment.role) && <option value={segment.role}>{segment.role}</option>}
            </Form.Select> : <Form.Control className="mt-2" value={segment.role || ''} onChange={(event) => updateSegment(index, { role: event.target.value })} placeholder="Role" aria-label={`Segment ${index + 1} role`} />}
            {draft.type === 'debate' && /^moderator$/i.test(segment.role || '') && <small>Speaker: assigned moderator</small>}
            {draft.type === 'debate' && isDebaterSegment(segment) && <small>Speakers: assigned Pro and Con debaters. Limits below are totals, divided equally and rounded down to whole seconds for each debater.</small>}
            {draft.type === 'debate' && !isDebaterSegment(segment) && !/^moderator$/i.test(segment.role || '') && <Form.Select className="mt-2" value={segment.speakerId || ''} onChange={(event) => updateSegment(index, { speakerId: event.target.value })}>
              <option value="">Choose speaker</option>
              {Array.from(new Map([
                ...users, ...(draft.sides?.pro || []), ...(draft.sides?.con || []),
              ].filter(Boolean).map(user => [String(user.tmAuthUserId || user.userId || user.id || user._id), user])).entries())
                .map(([id, user]) => <option key={id} value={id}>{displayName(user)}</option>)}
            </Form.Select>}
            <div className="d-flex flex-wrap gap-2 mt-2">
              {['green', 'amber', 'red', 'total'].map(color => <Form.Label key={color}>
                <span className="d-block">{color.charAt(0).toUpperCase() + color.slice(1)}</span>
                <Form.Control size="sm" style={{ width: '12ch' }} maxLength={10}
                  value={segment[color] || ''} aria-label={`Segment ${index + 1} ${color}`}
                  onChange={event => updateSegment(index, { [color]: event.target.value })} />
              </Form.Label>)}
            </div>
          </div>)}
          </div>
          <div className="d-flex justify-content-between align-items-center gap-2 mb-2" role="group" aria-label="Activity editor actions">
            <Button size="sm" variant="outline-secondary" onClick={() => setDraft({ ...draft, segments: [...(draft.segments || []), { id: `segment-${Date.now()}`, title: '', role: '', speakerId: '' }] })}>Add segment</Button>
            <div className="d-flex align-items-center gap-2">
            <Button size="sm" disabled={isLocked} data-save-activity aria-label="Save activity at bottom" title="Save activity"
              onClick={() => void saveAndClose()}><i className="bi bi-floppy" aria-hidden="true" /></Button>
            <Button size="sm" variant="outline-secondary" disabled={saving} aria-label="Close activity editor at bottom"
              onClick={() => setShowEditor(false)}><i className="bi bi-x-lg me-1" aria-hidden="true" />Close</Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
